import { useEventListener } from "@vueuse/core";
import {
  computed,
  isRef,
  type MaybeRef,
  ref,
  type UnwrapRef,
  watch,
  type WatchStopHandle
} from "vue";
import { type LocationQueryValue, useRoute, useRouter } from "vue-router";

type RouteMethod = "push" | "replace";

interface UseRouterSyncOptions<T extends LocationQueryValue> {
  method: MaybeRef<RouteMethod>;
  autoSync: boolean;
  defaultValue: T;
  validator: (value: unknown) => value is T;
}

/**
 * Create a ref that will be synced with router query parameters.
 *
 * If a options.validator is provided, the query parameter will be set to the options.defaultValue if the validator returns false.
 *
 * @param {string} param Query parameter name
 * @param {Partial<UseRouterSyncOptions>} options
 */
export function useRouterQuerySync<T extends LocationQueryValue>(
  param: string,
  options: Partial<UseRouterSyncOptions<T>> = {}
) {
  const { autoSync = true, defaultValue = "" as T, validator = () => true } = options;

  const router = useRouter();
  const route = useRoute();

  const routerMethod = computed<RouteMethod>(() => {
    if (!options.method) {
      return "push";
    }

    return isRef(options.method) ? options.method.value : options.method;
  });

  const value = ref<T>(defaultValue);

  // Ensure changes made to the query param get applied to the ref
  watch(
    () => route.query[param],
    (newValue) => {
      if (validator(newValue)) {
        value.value = newValue as UnwrapRef<T>;
      }
    },
    { immediate: true, deep: true }
  );

  const sync = (newValue: UnwrapRef<typeof value>) => {
    // Update ref
    value.value = newValue;

    // Update query param
    const query = { ...route.query };
    query[param] = newValue;
    router[routerMethod.value]({ query });
  };

  let stopSync: WatchStopHandle = () => undefined;

  if (autoSync) {
    stopSync = watch(value, sync);
  }

  // Ensure the query parameter is set to the default value if it gets messed up by back/forward actions
  useEventListener("popstate", () => {
    if (defaultValue === undefined) {
      return;
    }

    if (!validator(route.query[param])) {
      router.replace({ query: { ...route.query, [param]: defaultValue } });
    }
  });

  return {
    value,
    /** Router method, in case you need to access or overwrite it after composable instantiation */
    routerMethod,
    /** Manually sync the parameter - will update both the ref and route query parameter. */
    sync,
    /** Stop automatic sync */
    stopSync
  };
}
